import React, { useCallback, useEffect, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { darken, lighten } from "@mui/material/styles";
import Header from "../Header";
import Footer from "../Footer";
import dayjs from "dayjs";
import WeekView from "./WeekView";
import MonthView from "./MonthView";

const NOISE_SVG = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

const TRANSITION = "transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)";

const Calendar = () => {
    const theme = useTheme();
    const hex = theme.palette.primary.main;

    const [view, setView] = useState<"week" | "month">("week");
    const [sheetPosition, setSheetPosition] = useState<"up" | "down">("up");
    const [transitioning, setTransitioning] = useState(false);

    const today = dayjs()
        .set("hour", 0)
        .set("minute", 0)
        .set("second", 0)
        .set("millisecond", 0);
    const [selectedDay, setSelectedDay] = useState(today);
    const [scrollY, setScrollY] = useState(0);
    const [monthScrollY, setMonthScrollY] = useState(0);

    useEffect(() => {
        sessionStorage.removeItem("slot_start");
        sessionStorage.removeItem("slot_end");
    }, []);

    // Week → Month: slide sheet down, then switch view
    const handleSwitchToMonth = useCallback(() => {
        setTransitioning(true);
        setSheetPosition("down");
    }, []);

    // Month → Week: switch view with sheet at bottom, then animate up
    const handleSwitchToWeek = useCallback(() => {
        setView("week");
        setSheetPosition("down");
        setTransitioning(true);
        // Double rAF to ensure DOM has rendered with sheet at bottom
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setSheetPosition("up");
            });
        });
    }, []);

    const handleMonthDaySelect = useCallback(
        (val: dayjs.Dayjs) => {
            setSelectedDay(val);
            handleSwitchToWeek();
        },
        [handleSwitchToWeek]
    );

    const handleTransitionEnd = useCallback(() => {
        if (sheetPosition === "down" && view === "week") {
            // Sheet finished sliding down — switch to month view
            setView("month");
            setTransitioning(false);
        } else if (sheetPosition === "up" && view === "week") {
            // Sheet finished sliding up — cleanup
            setTransitioning(false);
        }
    }, [sheetPosition, view]);

    const effectiveScrollY = view === "week" ? scrollY : monthScrollY;

    return (
        <>
            <Header scrollY={effectiveScrollY} />

            {/* Fixed gradient backdrop — always visible */}
            <Box
                sx={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 0,
                    background: `linear-gradient(180deg, ${lighten(hex, 0.08)} 0%, ${darken(hex, 0.08)} 100%)`,
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        opacity: 0.04,
                        backgroundImage: NOISE_SVG,
                        backgroundRepeat: "repeat",
                        backgroundSize: "128px 128px",
                        pointerEvents: "none",
                    },
                }}
            />

            {/* WeekView sheet — slides up/down */}
            {(view === "week" || transitioning) && (
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 2,
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0,
                        transform:
                            sheetPosition === "up"
                                ? "translateY(0)"
                                : "translateY(100vh)",
                        transition: TRANSITION,
                        willChange: transitioning ? "transform" : "auto",
                    }}
                    onTransitionEnd={handleTransitionEnd}
                >
                    <WeekView
                        selectedDay={selectedDay}
                        setSelectedDay={setSelectedDay}
                        onSwitchToMonth={handleSwitchToMonth}
                        onScroll={setScrollY}
                    />
                </Box>
            )}

            {/* MonthView — shown when sheet is down */}
            {view === "month" && !transitioning && (
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0,
                        "@keyframes monthFadeIn": {
                            from: { opacity: 0 },
                            to: { opacity: 1 },
                        },
                        animation: "monthFadeIn 0.3s ease 0.1s both",
                    }}
                >
                    <MonthView
                        selectedDay={selectedDay}
                        setSelectedDay={handleMonthDaySelect}
                        onSwitchToWeek={handleSwitchToWeek}
                        onScroll={setMonthScrollY}
                    />
                </Box>
            )}

            <Footer tab="calendar" />
        </>
    );
};

export default Calendar;
